/**
 * Calendar-date helpers. All functions work on 'YYYY-MM-DD' strings and use UTC internally,
 * so results never shift with the server or browser time zone.
 */

export type ISODate = string;

const DAY_MS = 86_400_000;

export function parseISODate(d: ISODate): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) throw new Error(`Invalid date: ${d}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function toISODate(date: Date): ISODate {
  return date.toISOString().slice(0, 10);
}

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = parseISODate(value);
  return toISODate(d) === value;
}

export function addDays(d: ISODate, n: number): ISODate {
  return toISODate(new Date(parseISODate(d).getTime() + n * DAY_MS));
}

export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((parseISODate(a).getTime() - parseISODate(b).getTime()) / DAY_MS);
}

/** ISO weekday: 1 = Monday ... 7 = Sunday */
export function isoWeekday(d: ISODate): number {
  const w = parseISODate(d).getUTCDay();
  return w === 0 ? 7 : w;
}

export function isWeekend(d: ISODate): boolean {
  return isoWeekday(d) >= 6;
}

/** Monday of the week containing d. */
export function weekStart(d: ISODate): ISODate {
  return addDays(d, 1 - isoWeekday(d));
}

export function isoWeek(d: ISODate): { year: number; week: number } {
  const date = parseISODate(d);
  const thursday = new Date(date.getTime() + (4 - (isoWeekday(d))) * DAY_MS);
  const year = thursday.getUTCFullYear();
  const jan1 = Date.UTC(year, 0, 1);
  const week = Math.floor((thursday.getTime() - jan1) / DAY_MS / 7) + 1;
  return { year, week };
}

export function eachDay(from: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  const n = diffDays(to, from);
  for (let i = 0; i <= n; i++) out.push(addDays(from, i));
  return out;
}

/**
 * The days between two dates (both included) that fall on the chosen weekdays (ISO: 1 Monday ... 7 Sunday), for
 * booking several days at once. The list stops one entry after `limit`, so a far-away end date never builds a long
 * list; the caller sees `length > limit` and refuses.
 */
export function daysOnWeekdays(from: ISODate, to: ISODate, weekdays: readonly number[], limit = 60): ISODate[] {
  const out: ISODate[] = [];
  if (to < from || weekdays.length === 0) return out;
  let d = from;
  for (let i = 0; i < 800 && d <= to && out.length <= limit; i++) {
    if (weekdays.includes(isoWeekday(d))) out.push(d);
    d = addDays(d, 1);
  }
  return out;
}

export function monthStart(d: ISODate): ISODate {
  return d.slice(0, 7) + '-01';
}

export function monthEnd(d: ISODate): ISODate {
  const date = parseISODate(monthStart(d));
  const next = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
  return toISODate(new Date(next.getTime() - DAY_MS));
}

export function addMonths(d: ISODate, n: number): ISODate {
  const date = parseISODate(d);
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + n, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(date.getUTCDate(), lastDay));
  return toISODate(target);
}

/** One formatter per time zone: creating an Intl.DateTimeFormat costs far more than formatting (lists call this per row). */
const DAY_FORMATS = new Map<string, Intl.DateTimeFormat>();

export function todayISO(now: Date = new Date(), timeZone = 'Europe/Berlin'): ISODate {
  let format = DAY_FORMATS.get(timeZone);
  if (!format) {
    // en-CA formats as YYYY-MM-DD
    format = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    DAY_FORMATS.set(timeZone, format);
  }
  return format.format(now);
}

export function minDate(a: ISODate, b: ISODate): ISODate {
  return a < b ? a : b;
}

export function maxDate(a: ISODate, b: ISODate): ISODate {
  return a > b ? a : b;
}

const WEEKDAYS: Record<'de' | 'en' | 'tr', string[]> = {
  de: ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  tr: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
};
const WEEKDAYS_SHORT: Record<'de' | 'en' | 'tr', string[]> = {
  de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  tr: ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'],
};
const MONTHS: Record<'de' | 'en' | 'tr', string[]> = {
  de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
};

export type Lang3 = 'de' | 'en' | 'tr';

export function weekdayName(d: ISODate, lang: Lang3 = 'de'): string {
  return WEEKDAYS[lang][isoWeekday(d) - 1];
}

export function weekdayShort(d: ISODate, lang: Lang3 = 'de'): string {
  return WEEKDAYS_SHORT[lang][isoWeekday(d) - 1];
}

export function monthName(monthIndex0: number, lang: Lang3 = 'de'): string {
  return MONTHS[lang][monthIndex0];
}

/** "Freitag, 1. August 2025" (de) / "Friday, 1 August 2025" (en) / "1 Ağustos 2025 Cuma" (tr) */
export function longDate(d: ISODate, lang: Lang3 = 'de'): string {
  const date = parseISODate(d);
  const day = date.getUTCDate();
  const month = monthName(date.getUTCMonth(), lang);
  const year = date.getUTCFullYear();
  if (lang === 'de') return `${weekdayName(d, lang)}, ${day}. ${month} ${year}`;
  if (lang === 'tr') return `${day} ${month} ${year} ${weekdayName(d, lang)}`;
  return `${weekdayName(d, lang)}, ${day} ${month} ${year}`;
}

/** "24.09.2026" */
export function shortDate(d: ISODate): string {
  return `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;
}

/** "August 2025" */
export function monthLabel(d: ISODate, lang: Lang3 = 'de'): string {
  const date = parseISODate(d);
  return `${monthName(date.getUTCMonth(), lang)} ${date.getUTCFullYear()}`;
}
